/** biome-ignore-all lint/suspicious/noExplicitAny: please remove any usages */
import {type JSX, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {saveEventThunk} from '~/CreateFabricInvitation/api/event';
import {navigateToRoute, useCurrentRoute} from '~/CreateFabricInvitation/navigation';
import {GuestOptionsForm} from '~/common/fabric/EventGuests/components/GuestOptions/GuestOptionsForm';
import {UPDATE_EVENT_DETAILS} from '~/common/fabric/reducers/details/constants';
import {selectRsvpOff} from '~/common/fabric/reducers/details/selectors';
import {SET_GUEST_OPTIONS} from '~/common/fabric/reducers/guestOptions/constants';
import {
  selectAllowBringPeople,
  selectAllowKids,
  selectAllowMaybeRSVPs,
  selectHideGuestlist,
  selectLimitPerGuest,
  selectMaxGuestList,
  selectShowMaxGuest,
} from '~/common/fabric/reducers/guestOptions/selectors';
import {CREATE_EDIT_POLL} from '~/common/fabric/reducers/widgets';
import {webViewCloseGuestOptionsMenu} from '~/common/fabric/webview/webview';
import {isFabricWebview} from '~/common/utils/webview';
import {BackButton} from '~library/atoms/BackButton';
import {ResponsivePanel} from '~library/organisms/ResponsivePanel';

import '../Modals/GuestOptions.sass';

interface GuestOptionsState {
  hideGuestlist: boolean;
  allowBringPeople: boolean;
  limitPerGuest: number;
  maxGuestList: number | null;
  showMaxGuest: boolean;
  allowKids: boolean;
  allowMaybeRSVPs: boolean;
  rsvpOff: boolean;
  loading: boolean;
}

interface GuestOptionsOverlayProps {
  closeOverlay: () => void;
  isOpen?: boolean;
  fullScreen?: boolean;
}

export const GuestOptionsOverlay: ({
  closeOverlay,
  isOpen,
  fullScreen,
}: GuestOptionsOverlayProps) => JSX.Element = ({
  closeOverlay,
  isOpen = true,
  fullScreen = false,
}) => {
  const store = useStore();
  const dispatch = useDispatch();
  const isInitialLoad = useRef(true);
  const allowMaybeRSVPs = useSelector(selectAllowMaybeRSVPs);
  const allowBringPeople = useSelector(selectAllowBringPeople);
  const allowKids = useSelector(selectAllowKids);
  const limitPerGuest = useSelector(selectLimitPerGuest);
  const maxGuestList = useSelector(selectMaxGuestList);
  const showMaxGuest = useSelector(selectShowMaxGuest);
  const hideGuestlist = useSelector(selectHideGuestlist);
  const initialRsvpOff = useSelector(selectRsvpOff);
  const currentRoute = useCurrentRoute();
  const isStep2 = currentRoute?.name === 'details';
  const isGuestOptionsPage = window.location.href.includes('guest-options');

  const optsFromBackend: GuestOptionsState = useMemo(
    () => ({
      allowMaybeRSVPs,
      hideGuestlist,
      allowBringPeople,
      allowKids,
      limitPerGuest,
      maxGuestList,
      showMaxGuest,
      rsvpOff: initialRsvpOff,
      loading: false,
    }),
    [
      allowMaybeRSVPs,
      hideGuestlist,
      allowBringPeople,
      allowKids,
      limitPerGuest,
      maxGuestList,
      showMaxGuest,
      initialRsvpOff,
    ]
  );

  const [optsFromState, setOptsFromState] = useState<GuestOptionsState>({
    ...optsFromBackend,
  });

  const handleRsvpOffChange = useCallback((rsvpOff: boolean) => {
    setOptsFromState((prevState: GuestOptionsState): GuestOptionsState => {
      if (rsvpOff) {
        return {
          ...prevState,
          allowBringPeople: false,
          maxGuestList: 0,
          showMaxGuest: false,
          allowMaybeRSVPs: false,
        };
      }
      const limitPerGuest = prevState.limitPerGuest || 9;
      return {
        ...prevState,
        allowBringPeople: true,
        limitPerGuest,
        allowMaybeRSVPs: true,
      };
    });
  }, []);

  const resetOptions = (oldOptions: GuestOptionsState) => {
    setOptsFromState({
      hideGuestlist: oldOptions.hideGuestlist,
      allowBringPeople: oldOptions.allowBringPeople,
      limitPerGuest: oldOptions.limitPerGuest,
      maxGuestList: oldOptions.maxGuestList,
      showMaxGuest: oldOptions.showMaxGuest,
      allowKids: oldOptions.allowKids,
      allowMaybeRSVPs: oldOptions.allowMaybeRSVPs,
      rsvpOff: oldOptions.rsvpOff,
      loading: false,
    });
  };

  const handleClose = () => {
    if (closeOverlay) {
      closeOverlay();
      return;
    }
    if (isFabricWebview()) {
      webViewCloseGuestOptionsMenu();
      navigateToRoute('exit', store, true);
    } else {
      navigateToRoute('back', store);
    }
  };

  const handleCancel = () => {
    resetOptions(optsFromBackend);
    handleClose();
  };

  const handleSave = async () => {
    setOptsFromState((prevState) => ({...prevState, loading: true}));

    try {
      dispatch({
        type: UPDATE_EVENT_DETAILS,
        // biome-ignore lint/style/useNamingConvention: existing error
        payload: {rsvp_off: optsFromState.rsvpOff},
      });

      if (optsFromState.rsvpOff) {
        // If turning off RSVPs, clear any existing poll data
        store.dispatch({type: CREATE_EDIT_POLL, payload: {polls: []}});
      }

      dispatch({
        type: SET_GUEST_OPTIONS,
        payload: {
          hideGuestlist: optsFromState.hideGuestlist && !optsFromState.rsvpOff,
          allowBringPeople: optsFromState.allowBringPeople,
          limitPerGuest: optsFromState.rsvpOff ? 0 : optsFromState.limitPerGuest,
          maxGuestList: optsFromState.maxGuestList || 0,
          allowKids: optsFromState.allowKids && !optsFromState.rsvpOff,
          allowMaybeRSVPs: optsFromState.allowMaybeRSVPs,
        },
      });

      if (!isStep2) {
        // @ts-expect-error function not typed
        await dispatch(saveEventThunk());
      }

      setOptsFromState((prevState) => ({...prevState, loading: false}));
      handleClose();
    } catch (_) {
      setOptsFromState((prevState) => ({...prevState, loading: false}));
    }
  };

  useEffect(() => {
    const hasPlusOnes = optsFromState.allowBringPeople || (optsFromState.limitPerGuest ?? 0) > 0;
    if (!hasPlusOnes) {
      setOptsFromState((prevState) => ({...prevState, allowKids: false}));
    }
  }, [optsFromState.allowBringPeople, optsFromState.limitPerGuest]);

  useEffect(() => {
    setOptsFromState((prev) => {
      const allowBringPeople = optsFromBackend.rsvpOff
        ? false
        : optsFromBackend.allowBringPeople || (optsFromBackend.limitPerGuest ?? 0) > 0;

      return {
        ...prev,
        ...optsFromBackend,
        loading: false,
        allowBringPeople,
        allowKids: allowBringPeople ? optsFromBackend.allowKids : false,
      };
    });
  }, [optsFromBackend]);

  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    handleRsvpOffChange(optsFromState.rsvpOff);
  }, [optsFromState.rsvpOff, handleRsvpOffChange]);

  if (fullScreen) {
    return (
      <div className="edit-flow__guest-options">
        <div
          className="edit-flow__guest-options__content"
          style={{
            flexDirection: 'column',
            display: 'flex',
            marginBottom: fullScreen ? '2rem' : undefined,
          }}
        >
          <BackButton data-qa-id="link-my-events" onClick={handleCancel} text="Back to event" />
          <GuestOptionsForm
            options={optsFromState}
            setOptions={setOptsFromState}
            handleSave={handleSave}
            isGuestOptionsPage={isGuestOptionsPage}
          />
        </div>
      </div>
    );
  }

  return (
    <ResponsivePanel
      isOpen={isOpen}
      onClose={handleCancel}
      title="RSVP options"
      modalId="create-flow__overlay"
      showCover
      renderMobileViewAs="bottom-sheet"
      hideHeaderDivider
      childProvidesSideMargin
      portalMode
      disableOverlayClick
    >
      <GuestOptionsForm
        options={optsFromState}
        setOptions={setOptsFromState}
        handleSave={handleSave}
        isGuestOptionsPage={isGuestOptionsPage}
      />
    </ResponsivePanel>
  );
};
